import { createContext, useContext } from 'react'
import { isChainPointerRow, isPointerRow, pointerKindOf, isMiniappFolder, isMiniappTabType, isProjectFolder, parseMiniappIcon, parseMiniappIconRev, FIREBIRD_DB_EXTS, type MiniappIconSpec, type StructuralFolderKind } from '@papan/types'
import { MiniappIcon } from './MiniappIcon'
import { PDAppIcon, type GlyphIcon } from './PDAppIcon'
import {
  Building2,
  Database,
  File,
  FolderArchive,
  FileCode,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  FolderCog,
  HardDrive,
  Image,
  LayoutGrid,
  Link2,
  Presentation,
  Settings,
  SquareLibrary,
  UserRound,
  Users,
  UsersRound,
  Wifi,
} from 'lucide-react'

/**
 * The drive's STRUCTURAL folders (#889 — `structuralFolderKind`, @papan/types) draw
 * the icon their place has in the Drive sidebar, in their own colour, so a
 * person tells the drive's scaffolding from a folder they made (and doesn't share
 * one as an app). Library / Projects / PDApps / Shared / My Space match the sidebar;
 * the rest (System, Persons, a source org under Shared) get the nearest glyph.
 */
/**
 * Their colour: the app's indigo, NOT the folder yellow (owner, 2026-10-01) — some
 * people could not tell the drive's own folders from their own by glyph alone, and
 * indigo against yellow stays distinct for the common colour-vision deficiencies
 * (yellow vs amber/orange is the hard pair). Theme-aware: a deeper shade on light.
 */
const STRUCTURAL_FOLDER_CLASS = 'text-indigo-400 [.theme-light_&]:text-indigo-600'

const STRUCTURAL_FOLDER_ICON: Record<StructuralFolderKind, GlyphIcon> = {
  'drive-root': HardDrive,
  library: SquareLibrary,
  projects: LayoutGrid,
  pdapps: PDAppIcon,
  persons: UsersRound,
  shared: Users,
  system: Settings,
  'person-space': UserRound,
  'shared-org': Building2,
  'legacy-container': FolderCog,
}

// ---------------------------------------------------------------------------
// Registry types
// ---------------------------------------------------------------------------

export interface FileIconEntry {
  /** Widened from `LucideIcon` so a hand-drawn glyph (PDAppIcon) can register
   *  here — a plain function component is not assignable to lucide's
   *  forwardRef type. Every lucide icon still satisfies it. */
  icon: GlyphIcon
  /** Tailwind class for color (e.g. "text-green-500") */
  className?: string
  /** Inline hex color — takes precedence over className when both set */
  color?: string
  /**
   * Tailwind classes for the icon's backdrop chip
   * (bg/rounded/padding/ring utilities). Semantics depend on which
   * registry the entry lives in:
   *   - on an OVERLAY entry → styles the small corner BADGE chip
   *     (e.g. `.chain` → grey rounded pill around the link badge);
   *   - on a base entry → reserved for a future per-type tile default
   *     on the outer wrapper (not currently consumed).
   * The consumer-supplied `<FileIcon bgClassName="…">` prop styles the
   * OUTER wrapper independently and never collides with this.
   */
  bgClassName?: string
}

export type FileIconRegistry = Record<string, FileIconEntry>

// ---------------------------------------------------------------------------
// Default registry — single source of truth for every file-type icon
// ---------------------------------------------------------------------------

/** SEMANTIC — DO NOT BRAND. Every colour here identifies a FILE TYPE (green =
 *  spreadsheet, violet = parquet, purple = video, …), not the product. A brand
 *  swapping its primary to green must not turn parquet files green, so these
 *  stay on fixed Tailwind palette colours. Reviewed during the white-label
 *  colour audit and deliberately left alone. */
export const defaultFileIconRegistry: FileIconRegistry = {
  // Spreadsheets
  csv:      { icon: FileSpreadsheet, color: '#dcaa4c' },
  tsv:      { icon: FileSpreadsheet, color: '#d2b48c' },
  xlsx:     { icon: FileSpreadsheet, className: 'text-green-500' },
  xls:      { icon: FileSpreadsheet, className: 'text-green-500' },
  ods:      { icon: FileSpreadsheet, className: 'text-green-500' },
  fods:     { icon: FileSpreadsheet, className: 'text-green-500' },
  ots:      { icon: FileSpreadsheet, className: 'text-green-500' },
  parquet:  { icon: FileSpreadsheet, className: 'text-violet-400' },

  // Documents
  docx:     { icon: FileText, color: '#2b7cd3' },
  doc:      { icon: FileText, color: '#2b7cd3' },
  odt:      { icon: FileText, color: '#2b7cd3' },
  fodt:     { icon: FileText, color: '#2b7cd3' },
  ott:      { icon: FileText, color: '#2b7cd3' },
  rtf:      { icon: FileText, color: '#2b7cd3' },
  pdf:      { icon: FileText, color: '#e5252a' },

  // Presentations
  pptx:     { icon: Presentation, color: '#d04423' },
  ppt:      { icon: Presentation, color: '#d04423' },
  odp:      { icon: Presentation, color: '#d04423' },
  fodp:     { icon: Presentation, color: '#d04423' },
  otp:      { icon: Presentation, color: '#d04423' },

  // Code / markup
  json:     { icon: FileCode, className: 'text-orange-500' },
  md:       { icon: FileCode, color: '#0fa4e0' },
  markdown: { icon: FileCode, color: '#0fa4e0' },
  html:     { icon: FileCode, color: '#ff6c61' },
  htm:      { icon: FileCode, color: '#ff6c61' },
  xml:      { icon: FileCode, className: 'text-blue-400' },
  yaml:     { icon: FileCode, className: 'text-zinc-400' },
  yml:      { icon: FileCode, className: 'text-zinc-400' },
  py:       { icon: FileCode, color: '#4b8bbe' },

  // Text
  txt:      { icon: FileText, className: 'text-zinc-400' },
  log:      { icon: FileText, className: 'text-zinc-400' },
  ppn:      { icon: FileText, className: 'text-blue-500' },

  // Images
  png:      { icon: Image, className: 'text-rose-400' },
  jpg:      { icon: Image, className: 'text-rose-400' },
  jpeg:     { icon: Image, className: 'text-rose-400' },
  gif:      { icon: Image, className: 'text-rose-400' },
  webp:     { icon: Image, className: 'text-rose-400' },
  svg:      { icon: Image, className: 'text-rose-400' },
  bmp:      { icon: Image, className: 'text-rose-400' },
  ico:      { icon: Image, className: 'text-rose-400' },

  // Video
  mp4:      { icon: FileVideo, className: 'text-purple-400' },
  webm:     { icon: FileVideo, className: 'text-purple-400' },
  ogg:      { icon: FileVideo, className: 'text-purple-400' },
  mov:      { icon: FileVideo, className: 'text-purple-400' },

  // Archives
  zip:      { icon: FolderArchive, className: 'text-amber-500' },

  // Databases
  db:         { icon: Database, className: 'text-blue-400' },
  sqlite:     { icon: Database, className: 'text-blue-400' },
  sqlite3:    { icon: Database, className: 'text-blue-400' },
  // Firebird — every extension of the one list (`FIREBIRD_DB_EXTS`: fdb, gdb, firebird)
  ...Object.fromEntries(FIREBIRD_DB_EXTS.map((ext) => [ext.slice(1), { icon: Database, className: 'text-red-400' }])),

  // Special
  sqlparz:    { icon: Database, className: 'text-green-400' },
  folder:     { icon: Folder, className: 'text-yellow-500' },
  miniapp:       { icon: PDAppIcon, className: 'text-pink-400' },      // editor TAB (type='miniapp') — the pink app icon, matching the .miniapp folder row
  'miniapp-folder': { icon: PDAppIcon, className: 'text-pink-400' },  // .miniapp folder row in the explorer (pinker than .project purple)
  workpapan:  { icon: LayoutGrid, className: 'text-purple-400' },
  link:       { icon: Link2, className: 'text-purple-400' },
}

const DEFAULT_FALLBACK: FileIconEntry = { icon: File, className: 'text-zinc-400' }

// ---------------------------------------------------------------------------
// Overlay registry — small corner badge composited ON TOP of the base icon
// (the base glyph is preserved; this only adds a badge). Distinct from the
// base registry: overlays are keyed by mount kind, not file extension.
// Share primitive (docs/plans/2026-05-15_SHARE_PRIMITIVE.md):
//   .shared = the cross-tenant crossing → network badge (matches the
//             Settings → Network surface that governs these crossings)
//   .chain  = same-tenant traversable pointer → link/alias badge
// ---------------------------------------------------------------------------

export type FileIconOverlay = 'chain' | 'shared'

export const defaultOverlayRegistry: Record<FileIconOverlay, FileIconEntry> = {
  // .chain — signature look: small SOLID grey rounded-pill chip in
  // the corner with the sky link glyph inside. Opaque zinc-500 reads
  // cleanly on both dark + light surfaces.
  chain:  {
    icon: Link2,
    // Chip bg + glyph color live in `.file-chip-chain` (apps/web index.css) so
    // both flip in light mode; the class sets `color`, which the Link2 glyph
    // picks up via currentColor.
    bgClassName: 'file-chip-chain rounded-full p-0.5',
  },
  // .shared — Wifi glyph (cross-tenant crossing reads as "received
  // from over the network") with #00c950 green on a slate-700 chip.
  // Paired with the owner-side SHARING badge which uses the same
  // slate chip but a Share2 glyph in purple to stay visually distinct.
  shared: {
    icon: Wifi,
    // Chip bg + glyph color live in `.file-chip-shared` (apps/web index.css);
    // the class sets `color`, which the Wifi glyph picks up via currentColor,
    // so it themes for light mode.
    bgClassName: 'file-chip-shared rounded-full p-0.5',
  },
}

// ---------------------------------------------------------------------------
// Context for scoped overrides
// ---------------------------------------------------------------------------

const FileIconCtx = createContext<FileIconRegistry | null>(null)

/**
 * Wrap a subtree to override or extend the default icon registry.
 *
 * ```tsx
 * <FileIconProvider overrides={{ csv: { icon: Table2, className: 'text-amber-300' } }}>
 *   <DriveView />
 * </FileIconProvider>
 * ```
 */
export function FileIconProvider({
  overrides,
  children,
}: {
  overrides: Partial<FileIconRegistry>
  children: React.ReactNode
}) {
  const parent = useContext(FileIconCtx)
  const merged = { ...defaultFileIconRegistry, ...parent, ...overrides } as FileIconRegistry
  return <FileIconCtx.Provider value={merged}>{children}</FileIconCtx.Provider>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract lowercase extension from a filename. */
export function getFileExt(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
}

/**
 * Resolve the registry key for a given file. Exported for unit testing as pure
 * logic,
 * like its `resolveOverlayKey` sibling — the app-folder aliases regressed here
 * invisibly once already (#365).
 */
export function resolveKey(opts: {
  name?: string
  type?: string
  isFolder?: boolean
  isLink?: boolean
}, registry: FileIconRegistry): string {
  if (opts.isLink) return 'link'
  const ext = opts.name ? getFileExt(opts.name) : ''
  // An app folder (`.pdapp`/`.pda`/`.miniapp`) is tagged type='folder' on disk,
  // so detect it by NAME before the generic type match below — otherwise
  // type==='folder' short-circuits to the plain yellow folder icon. Shows the
  // "app" box.
  if (opts.isFolder && opts.name && isMiniappFolder(opts.name)) return 'miniapp-folder'
  // The workspace TAB path: `type` is the raw last extension ('pdapp'), which
  // is not an icon-registry key. Every alias normalizes onto the one 'miniapp'
  // key rather than getting its own entry, so all three share a single
  // downstream path — the custom-icon escape hatch below tests for that exact
  // key, and an alias key would silently render the generic Box glyph instead of
  // the app's real icon. Same class of miss as issue #365 itself.
  //
  // A row KNOWN to be a file is the sealed release zip, not an app, and the
  // pink tile beside a pane reading "Preview not available" states something
  // the app itself contradicts. `undefined` still gets the tile — most callers
  // do not know folder-ness, and the folder is overwhelmingly the common case.
  //
  // It returns '' (generic file glyph) rather than falling through, because the
  // legacy `miniapp` spelling IS a key in this registry: both the generic type
  // match below and the name-based resolution at the end would hand the tile
  // straight back, so only `.pdapp`/`.pda` would demote and the three aliases
  // would disagree again — the exact split this branch exists to close.
  if (opts.type && isMiniappTabType(opts.type)) {
    return opts.isFolder === false ? '' : 'miniapp'
  }
  // Use type if it matches the registry; otherwise fall through to name-based
  if (opts.type && opts.type !== 'unknown' && registry[opts.type]) return opts.type
  if (opts.name && isProjectFolder(opts.name)) return 'workpapan'
  // .shared is a metadata FILE on disk (is_folder=false). A FOLDER-target
  // share presents as a folder (the consumer passes isFolder=true, set by
  // the listing's presentsAsFolder); a FILE-target share presents as a
  // file — resolve the icon from the INNER extension (`report.pdf.shared`
  // → pdf). The Network overlay badge composites on top regardless via
  // resolveOverlayKey.
  if (ext === 'shared') {
    if (opts.isFolder) {
      // Folder-target pointer: resolve the INNER package identity so a
      // shared app / project is recognizable at a glance — same inner-name
      // strip the file-target branch below has always done. Kind-gated on
      // the row type ('shared', set by the listing mapper): a REAL folder
      // named `X.miniapp.shared` carries type='folder' (and already
      // short-circuited at the registry match above); a name-only consumer
      // keeps the plain folder icon.
      if (opts.type === 'shared') {
        const inner = opts.name!.slice(0, -'.shared'.length)
        if (isMiniappFolder(inner)) return 'miniapp-folder'
        if (isProjectFolder(inner)) return 'workpapan'
      }
      return 'folder'
    }
    const innerExt = getFileExt(opts.name!.slice(0, -'.shared'.length))
    // Normalize the app aliases onto the one registry key, same as the tab
    // branch above. A pointer row's isFolder is false on the detail endpoint,
    // so `MyApp.pdapp.shared` reaches HERE rather than the folder arm — and the
    // raw 'pdapp' is not a registry key, which drew the generic file glyph and
    // dropped the app's custom icon, while `.miniapp.shared` rendered it. The
    // folder arm above was already alias-aware, so this was the one surface
    // where the aliases still disagreed.
    if (isMiniappTabType(innerExt)) return 'miniapp'
    return innerExt || ''
  }
  if (opts.isFolder) return 'folder'
  return ext || opts.type || ''
}

/**
 * Resolve the overlay badge key, or null for none.
 *
 * Order:
 *  1. explicit `overlay` prop — authoritative (use when the displayed
 *     `name` has had its `.chain`/`.shared` extension stripped, or when
 *     the kind is known from another source).
 *  2. `fileType` prop — the row's DB row-kind column. Authoritative when
 *     set: 'shared'/'chain' ⇒ corresponding overlay; any other value
 *     ('file'/'folder') ⇒ NO overlay (the caller has the DB truth and
 *     knows this is not a mount artifact, even if the name happens to
 *     end in `.shared` because the user renamed a local file).
 *  3. legacy name-extension auto-detect — fires only when neither
 *     `overlay` nor `fileType` is passed (callers that haven't been
 *     updated to surface fileType, e.g. tree views).
 *
 * Only the mount ARTIFACT row carries the badge; synthesized children
 * *inside* a mount are ordinary files.
 */
export function resolveOverlayKey(opts: {
  name?: string
  overlay?: FileIconOverlay
  fileType?: string | null
}): FileIconOverlay | null {
  if (opts.overlay) return opts.overlay
  const kind = pointerKindOf(opts)
  if (kind) {
    // Extension is authoritative: a mount artifact renamed AWAY from its
    // `.shared`/`.chain` suffix is a plain file now — drop the badge (the
    // resolver/listing also demote it) — the ONE classifier, `isPointerRow`
    // (I110). Only gate when a name is in scope; nameless callers keep the
    // column-based overlay (back-compat).
    if (opts.name !== undefined && !isPointerRow(opts)) return null
    return kind
  }
  // Explicit non-mount fileType (anything passed that isn't shared/chain,
  // INCLUDING null) suppresses the legacy auto-detect — the caller has
  // the DB column in scope and knows this row is not a mount. Only
  // `fileType === undefined` (prop not passed at all) falls through.
  if (opts.fileType !== undefined) return null
  const ext = opts.name ? getFileExt(opts.name) : ''
  if (ext === 'chain' || ext === 'shared') return ext
  return null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface FileIconProps {
  /** Filename — extension is extracted automatically. */
  name?: string
  /** Explicit type key (e.g. "csv", "folder"). Overrides name-based detection. */
  type?: string
  isFolder?: boolean
  isLink?: boolean
  /**
   * Mount-kind corner badge composited on top of the base icon. The
   * base glyph (folder / pointed-at file type) is preserved. Most
   * authoritative — pass it when the displayed `name` has its
   * `.chain`/`.shared` extension stripped or the kind is known from a
   * non-DB source. Most callers should use `fileType` instead.
   */
  overlay?: FileIconOverlay
  /**
   * The row's DB row-kind column ('file' / 'folder' / 'shared' / 'chain').
   * Authoritative for the overlay decision after `overlay`. Pass this so
   * a user-renamed "foo.shared" file (fileType='file') does NOT get the
   * Network overlay — without this, the name-extension fallback would
   * incorrectly add the badge. Omit only on callers that don't have the
   * row's fileType in scope (legacy tree views fall back to name-detect).
   */
  fileType?: string | null
  /**
   * Tailwind classes for a tile-like background around the icon — pass
   * bg/rounded/padding/ring utilities here, e.g.
   * `"bg-zinc-800 rounded-md p-1"` or `"bg-amber-500/20 rounded-full p-1.5"`.
   * Omit to render with no backdrop (default — byte-identical to today).
   * Composes with `overlay`: a single wrapper hosts both, and the badge
   * still sits at the wrapper's bottom-right corner.
   */
  bgClassName?: string
  size?: number
  className?: string
  /**
   * Escape hatch for mini-app rows: when the resolved key is
   * `'miniapp-folder'` or `'miniapp'` AND `miniappIconName` is provided, the
   * registry Box glyph is replaced by the real `<MiniappIcon>` renderer using
   * this spec (null → name-derived default). Overlay/backdrop wrapping is
   * preserved around it. Absent → unchanged (registry Box icon).
   */
  miniappIconSpec?: MiniappIconSpec | null
  /** Display name that drives the derived-default mini-app icon fallback. */
  miniappIconName?: string
  /** `.miniapp` folder id — lets an ASSET icon load its server-composited PNG. */
  miniappIconRefId?: string | null
  /** The icon's cache-bust rev (`parseMiniappIconRev(metadataJson)`), paired with refId. */
  miniappIconRev?: string | null
  /**
   * The folder is one of the drive's structural folders — draw its sidebar icon
   * (folder yellow) instead of the plain folder. The caller knows the folder's
   * position (`structuralFolderKind` over its path); FileIcon never guesses it
   * from a name, since `Reports/Library` is an ordinary folder.
   */
  structural?: StructuralFolderKind | null
  /**
   * D17: a `.chain` that still NEEDS APPROVAL — the base glyph loses its colour and is drawn in
   * the theme's text colour, `themed-text` (white in the dark themes; the chain badge
   * stays), on every surface. Callers never set this by hand: they spread `fileIconProps(row)`.
   */
  pendingApproval?: boolean
}

/**
 * Every mini-app icon prop for a file row, in one spread:
 *
 *     <FileIcon name={f.name} isFolder={f.isFolder} {...miniappIconProps(f)} />
 *
 * USE THIS instead of hand-passing the props. A custom-IMAGE icon needs the spec
 * AND the folder id AND the rev; miss any one and MiniappIcon silently draws a
 * name-derived gradient instead — the icon looks broken rather than erroring. That
 * is exactly what happened: `miniappIconRefId`/`miniappIconRev` existed for a
 * while with ZERO callers, so every surface except the drive listing showed the
 * placeholder. The rev is already sitting in `metadataJson` at essentially every
 * call site (`stripToMiniappIcon` carries it across share/workspace boundaries) —
 * this just stops people forgetting to dig it out.
 *
 * Safe to spread on ANY row: FileIcon only uses these when the row actually
 * resolves to a mini-app, and `parseMiniappIcon` returns null for everything else.
 */
export function miniappIconProps(file: {
  id: string
  name: string
  metadataJson?: string | null
}): Pick<FileIconProps, 'miniappIconName' | 'miniappIconSpec' | 'miniappIconRefId' | 'miniappIconRev'> {
  return {
    miniappIconName: file.name,
    miniappIconSpec: parseMiniappIcon(file.metadataJson),
    miniappIconRefId: file.id,
    miniappIconRev: parseMiniappIconRev(file.metadataJson),
  }
}

/**
 * Centralized file-type icon.
 *
 * Resolves the icon from the registry in this order:
 *  1. `isLink` → "link"
 *  2. explicit `type` prop
 *  3. extension derived from `name`
 *  4. `isFolder` → "folder"
 *  5. fallback (generic File icon)
 *
 * Wrap with `<FileIconProvider>` to override icons in a subtree.
 */
/** D17: the colour a `.chain` awaiting approval is drawn in — WHITE in the dark themes (the theme's text colour, so it stays visible in the light theme); the badge keeps its own. */
const PENDING_APPROVAL_CLASS = 'themed-text'

/**
 * THE ONE way a listing row becomes `<FileIcon>` props — every surface (drive list / grid, mobile,
 * share view, pickers, dialogs) spreads `{...fileIconProps(row)}` instead of picking fields by hand,
 * so a row looks the same everywhere (I110: no per-surface drift).
 *  - a pointer is a pointer only by kind AND suffix (`resolveOverlayKey` gates on the name);
 *  - a `.chain` that still NEEDS APPROVAL (`chainNeedsApproval`) is drawn uncoloured (the theme's
 *    text colour, `PENDING_APPROVAL_CLASS`) with its chain badge.
 * Spread it FIRST, then any surface-only props (`size`, `className`).
 */
export function fileIconProps(row: {
  name: string
  type?: string
  isFolder?: boolean
  fileType?: string | null
  chainNeedsApproval?: boolean
}): Pick<FileIconProps, 'name' | 'type' | 'isFolder' | 'fileType' | 'pendingApproval'> {
  const isLiveChain = isChainPointerRow(row)
  return {
    name: row.name,
    ...(row.type !== undefined ? { type: row.type } : {}),
    isFolder: row.isFolder,
    fileType: row.fileType ?? null,
    ...(isLiveChain && row.chainNeedsApproval ? { pendingApproval: true } : {}),
  }
}

export function FileIcon({ name, type, isFolder, isLink, overlay, fileType, bgClassName, size = 14, className, miniappIconSpec, miniappIconName, miniappIconRefId, miniappIconRev, structural, pendingApproval }: FileIconProps) {
  const overrides = useContext(FileIconCtx)
  const registry = overrides ?? defaultFileIconRegistry
  const key = resolveKey({ name, type, isFolder, isLink }, registry)
  const entry = registry[key] || DEFAULT_FALLBACK

  // A structural folder draws its sidebar glyph in its own colour (STRUCTURAL_FOLDER_CLASS).
  const Icon = structural && key === 'folder' ? STRUCTURAL_FOLDER_ICON[structural] : entry.icon
  // A chain awaiting approval: the glyph loses its colour and class (drawn in the theme's text
  // colour, PENDING_APPROVAL_CLASS), badge kept.
  const style = pendingApproval ? undefined : (entry.color && !(structural && key === 'folder') ? { color: entry.color } : undefined)
  const isStructural = !!structural && key === 'folder'
  const cls = ['shrink-0', pendingApproval ? PENDING_APPROVAL_CLASS : (isStructural ? STRUCTURAL_FOLDER_CLASS : entry.className), className].filter(Boolean).join(' ')

  // Mini-app escape hatch: render the real icon renderer in place of the
  // registry Box glyph, at the same pixel size the glyph would use. Only
  // when the row resolves to a mini-app key and a display name is supplied.
  const useMiniappIcon = (key === 'miniapp-folder' || key === 'miniapp') && miniappIconName !== undefined
  const glyph = useMiniappIcon ? (
    <MiniappIcon spec={miniappIconSpec ?? null} name={miniappIconName!} size={size} refId={miniappIconRefId} rev={miniappIconRev} className={['shrink-0', className].filter(Boolean).join(' ')} />
  ) : (
    <Icon size={size} className={cls} style={style} />
  )

  const overlayKey = resolveOverlayKey({ name, overlay, fileType })
  const ov = overlayKey ? defaultOverlayRegistry[overlayKey] : null
  const needsWrapper = !!ov || !!bgClassName
  if (!needsWrapper) {
    // No badge AND no consumer backdrop → render exactly as before
    // (single element, no wrapper). Zero regression for ~12 existing
    // call sites.
    return glyph
  }

  // Outer wrapper: hosts the base icon, an optional consumer-supplied
  // backdrop (bgClassName prop), and (if any) the absolutely-positioned
  // badge. `relative` makes the badge positionable; `inline-flex
  // items-center justify-center` centres the icon inside any padding
  // the prop adds; `shrink-0` preserves the row flex behaviour every
  // existing consumer relies on. The OVERLAY's own bgClassName does NOT
  // apply here — it styles the badge chip separately.
  const wrapperCls = [
    'relative inline-flex items-center justify-center shrink-0',
    bgClassName,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span className={wrapperCls}>
      {glyph}
      {ov && (() => {
        // Corner badge — bottom-right of the wrapper. When the overlay
        // entry ships a bgClassName (e.g. `.chain`'s grey pill), the
        // badge sits inside a small chip; the chip's bg is its own
        // contrast so no drop-shadow is needed. When there's no chip
        // (e.g. `.shared` today), the bare lucide glyph gets a tiny
        // drop-shadow for legibility over arbitrary base glyphs in any
        // theme. pointer-events-none so it never intercepts row clicks.
        const OverlayIcon = ov.icon
        const overlaySize = Math.max(8, Math.round(size * 0.6))
        const iconCls = [ov.className].filter(Boolean).join(' ')
        const iconStyle = ov.color ? { color: ov.color } : undefined
        if (ov.bgClassName) {
          return (
            <span
              className={[
                'absolute -bottom-[3px] -right-[4px] inline-flex items-center justify-center pointer-events-none',
                ov.bgClassName,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <OverlayIcon
                size={overlaySize}
                aria-hidden
                className={iconCls}
                style={iconStyle}
                strokeWidth={2.5}
              />
            </span>
          )
        }
        return (
          <OverlayIcon
            size={overlaySize}
            aria-hidden
            className={[
              'absolute -bottom-[3px] -right-[4px] pointer-events-none',
              '[filter:drop-shadow(0_0_1px_rgba(0,0,0,0.85))]',
              iconCls,
            ]
              .filter(Boolean)
              .join(' ')}
            style={iconStyle}
            strokeWidth={2.5}
          />
        )
      })()}
    </span>
  )
}
